import React from 'react';
import { Calculator, Building2, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров амортизации
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  depreciationGroups
}) => {
  // Расчет текущей стоимости актива
  const assetCost = parseFloat(formData.assetCost) || 0;
  
  // Расчет текущего срока в годах
  const usefulLife = parseFloat(formData.usefulLife) || 0;
  
  // Расчет текущей ликвидационной стоимости
  const salvageValue = parseFloat(formData.salvageValue) || 0;
  
  // Обработчик изменения стоимости
  const handleAssetCostChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      assetCost: newValue
    }));
  };
  
  // Обработчик изменения срока использования
  const handleUsefulLifeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      usefulLife: newValue
    }));
  };
  
  // Обработчик изменения ликвидационной стоимости
  const handleSalvageValueChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      salvageValue: newValue
    }));
  };
  
  // Предопределенные суммы стоимости для быстрого выбора
  const predefinedAmounts = [
    { value: '100000', label: '100 тыс.' },
    { value: '500000', label: '500 тыс.' },
    { value: '1000000', label: '1 млн' },
    { value: '5000000', label: '5 млн' }
  ];
  
  // Предопределенные сроки использования для быстрого выбора
  const predefinedTerms = [
    { value: '3', label: '3 года' },
    { value: '5', label: '5 лет' },
    { value: '10', label: '10 лет' },
    { value: '20', label: '20 лет' }
  ];
  
  // Функция расчета
  const calculateDepreciation = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Стоимость основного средства */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="assetCostSlider" className="text-gray-600 dark:text-gray-400">Стоимость</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(assetCost, 0)}</span>
          </div>
          <input
            type="range"
            id="assetCostSlider"
            min="10000"
            max="10000000"
            step="10000"
            value={assetCost}
            onChange={handleAssetCostChange}
            aria-label="Стоимость основного средства"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, assetCost: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.assetCost === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Срок полезного использования */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="usefulLifeSlider" className="text-gray-600 dark:text-gray-400">Срок использования</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.usefulLife} {formatYearsLabel(parseFloat(formData.usefulLife) || 0)}
            </span>
          </div>
          <input
            type="range"
            id="usefulLifeSlider"
            min="1"
            max="30"
            step="1"
            value={usefulLife}
            onChange={handleUsefulLifeChange}
            aria-label="Срок полезного использования"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными сроками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedTerms.map(term => (
              <button
                key={term.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, usefulLife: term.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.usefulLife === term.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {term.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Ликвидационная стоимость (процент от первоначальной) */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="salvageValueSlider" className="text-gray-600 dark:text-gray-400">Ликвидационная стоимость</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatCurrency(salvageValue, 0)} 
              <span className="text-xs ml-1 text-gray-500 dark:text-gray-400">
                ({assetCost > 0 ? Math.round((salvageValue / assetCost) * 100) : 0}%)
              </span>
            </span>
          </div>
          <input
            type="range"
            id="salvageValueSlider"
            min="0"
            max={assetCost * 0.5}
            step={assetCost * 0.01}
            value={salvageValue}
            onChange={handleSalvageValueChange}
            aria-label="Ликвидационная стоимость"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с процентами от первоначальной стоимости */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[0, 5, 10, 20].map(percent => (
              <button
                key={percent}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  salvageValue: (parseFloat(prev.assetCost) * (percent / 100)).toString() 
                }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${Math.round((salvageValue / assetCost) * 100) === percent 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {percent}%
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор метода амортизации */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Метод амортизации</label>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, depreciationMethod: 'straight_line' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.depreciationMethod === 'straight_line' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Линейный</div>
              <div className="text-xs opacity-75">Равномерный</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, depreciationMethod: 'reducing_balance' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.depreciationMethod === 'reducing_balance' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Уменьшаемого</div>
              <div className="text-xs opacity-75">Ускоренный</div>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, depreciationMethod: 'sum_of_years' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.depreciationMethod === 'sum_of_years' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">По сумме лет</div>
              <div className="text-xs opacity-75">Дегрессивный</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, depreciationMethod: 'units_of_production' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.depreciationMethod === 'units_of_production' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Произв. метод</div>
              <div className="text-xs opacity-75">По выработке</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateDepreciation}
          icon={<Building2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать амортизацию
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default QuickAdjustmentSidebar;